
import { supabase } from '../client';
import { Form } from '@/components/dashboard/shops/types';
import { createDefaultFormFields } from './utils';

export async function getShopForms(shopId: string): Promise<Form[]> {
  try {
    if (!shopId) {
      console.error('[getShopForms] No shop ID provided');
      return [];
    }
    
    console.log(`[getShopForms] Fetching forms for shop: ${shopId}`);
    
    // S'assurer que la requête contient l'en-tête d'autorisation
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('shop_id', shopId);

    if (error) {
      console.error('[getShopForms] Error fetching forms:', error);
      throw error;
    }

    console.log(`[getShopForms] Retrieved ${data?.length || 0} forms`);
    return data || [];
  } catch (error) {
    console.error('[getShopForms] Exception in getShopForms:', error);
    return [];
  }
}

export async function getFormById(formId: string): Promise<Form | null> {
  try {
    if (!formId) {
      console.error('[getFormById] No form ID provided');
      return null;
    }
    
    console.log(`[getFormById] Fetching form with ID: ${formId}`);
    
    // Utilisation de la fonction RPC publique pour récupérer un formulaire
    // Cette fonction sera accessible sans authentification
    const { data, error } = await supabase
      .rpc('get_public_form', { form_id: formId });

    if (error) {
      console.error('[getFormById] Error fetching form using RPC:', error);
      // Fallback à la méthode standard au cas où la fonction RPC n'est pas disponible
      console.log('[getFormById] Falling back to direct query');
      const { data: directData, error: directError } = await supabase
        .from('forms')
        .select('*')
        .eq('id', formId)
        .single();
        
      if (directError) {
        console.error('[getFormById] Error with fallback query:', directError);
        throw directError;
      }
      
      return directData;
    }

    console.log(`[getFormById] Retrieved form:`, data);
    return data;
  } catch (error) {
    console.error('[getFormById] Exception in getFormById:', error);
    return null;
  }
}

export async function createForm(formData: Partial<Form>): Promise<{ success: boolean; data?: Form; error?: any }> {
  try {
    if (!formData.shop_id || !formData.organization_id) {
      console.error('[createForm] Missing required shop_id or organization_id');
      return { success: false, error: 'Missing required shop_id or organization_id' };
    }
    
    // Validate shop_id is a valid UUID
    if (typeof formData.shop_id !== 'string' || 
        !formData.shop_id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      console.error(`[createForm] Invalid shop_id: ${formData.shop_id}`);
      return { success: false, error: 'Invalid shop_id format' };
    }
    
    console.log(`[createForm] Creating form for shop: ${formData.shop_id}`, formData);
    
    // Assurez-vous que les champs fields existe
    if (!formData.fields || !Array.isArray(formData.fields) || formData.fields.length === 0) {
      formData.fields = createDefaultFormFields();
    }
    
    // Préparation des données à sauvegarder (seulement les champs qui existent dans la table)
    const formDataToSave = {
      name: formData.name,
      organization_id: formData.organization_id,
      shop_id: formData.shop_id,
      fields: formData.fields,
      cta_button_text: formData.cta_button_text,
      product_ids: formData.product_ids || []
    };
    
    const { data, error } = await supabase
      .from('forms')
      .insert([formDataToSave])
      .select()
      .single();

    if (error) {
      console.error('[createForm] Error creating form:', error);
      return { success: false, error };
    }

    console.log(`[createForm] Form created successfully:`, data);
    return { success: true, data };
  } catch (error) {
    console.error('[createForm] Exception in createForm:', error);
    return { success: false, error };
  }
}

export async function updateForm(formId: string, formData: Partial<Form>): Promise<{ success: boolean; error?: any }> {
  try {
    if (!formId) {
      console.error('[updateForm] No form ID provided');
      return { success: false, error: 'No form ID provided' };
    }
    
    console.log(`[updateForm] Updating form: ${formId}`, formData);
    
    // Préparation des données à sauvegarder (seulement les champs qui existent dans la table)
    const formDataToSave = {
      name: formData.name,
      fields: formData.fields,
      cta_button_text: formData.cta_button_text,
      product_ids: formData.product_ids || []
    };
    
    const { data, error } = await supabase
      .from('forms')
      .update(formDataToSave)
      .eq('id', formId)
      .select()
      .single();

    if (error) {
      console.error('[updateForm] Error updating form:', error);
      return { success: false, error };
    }

    console.log(`[updateForm] Form updated successfully:`, data);
    return { success: true };
  } catch (error) {
    console.error('[updateForm] Exception in updateForm:', error);
    return { success: false, error };
  }
}

export async function deleteForm(formId: string): Promise<{ success: boolean; error?: any }> {
  try {
    if (!formId) {
      console.error('[deleteForm] No form ID provided');
      return { success: false, error: 'No form ID provided' };
    }
    
    console.log(`[deleteForm] Deleting form: ${formId}`);
    
    const { error } = await supabase
      .from('forms')
      .delete()
      .eq('id', formId);

    if (error) {
      console.error('[deleteForm] Error deleting form:', error);
      return { success: false, error };
    }

    console.log(`[deleteForm] Form deleted successfully`);
    return { success: true };
  } catch (error) {
    console.error('[deleteForm] Exception in deleteForm:', error);
    return { success: false, error };
  }
}
